import ErrorComponent from "@/components/atoms/ErrorComponent";
import Loader from "@/components/atoms/Loader";
import SoftBadge from "@/components/atoms/SoftBadge";
import AreYouSurToContinueModal from "@/components/molecules/modals/AreYouSurToContinueModal";
import StoreActivitySectorModal from "@/components/molecules/modals/activitySectors/StoreActivitySectorModal";
import AllActivitySectorsHeader from "@/components/molecules/table-headers/AllActivitySectorsHeader";
import Table from "@/components/molecules/table/Table";
import { splitText } from "@/helpers/utils";
import ActivitySector from "@/interfaces/ActivitySector";
import { useTableState } from "@/hooks/useTableState";
import {
  useDestroyActivitySectorMutation,
  useGetActivitySectorsQuery,
} from "@/services/activitySectors";
import { faEdit, faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ColumnDef, TableState } from "@tanstack/react-table";
import { DateTime } from "luxon";
import { ElementRef, FC, Fragment, useMemo, useRef } from "react";
import { Button } from "react-bootstrap";
import { toast } from "react-toastify";

const ActivitySectorsView: FC = () => {
  const DeleteModalRef =
    useRef<ElementRef<typeof AreYouSurToContinueModal>>(null);
  const EditModalRef =
    useRef<ElementRef<typeof StoreActivitySectorModal>>(null);

  const [deleteActivitySectorAsync] = useDestroyActivitySectorMutation();

  const [state, setState] = useTableState<Partial<TableState>>(
    "activity-sectors",
    {
      pagination: {
        pageIndex: 0,
        pageSize: 15,
      },
      globalFilter: "",
    },
  );

  const { data, isLoading, isError, error } = useGetActivitySectorsQuery({
    withMetadata: true,
  });

  const onDelete = async (id: string) => {
    toast.promise(deleteActivitySectorAsync(id).unwrap(), {
      pending: "Suppression du secteur d'activité en cours...",
      success: {
        render() {
          DeleteModalRef.current?.showOrHiddenModal();
          return (
            <p className="mb-0 text-center">
              Le secteur d'activité a bien été supprimé 🤩
            </p>
          );
        },
      },
      error: {
        render() {
          return "Une erreur est survenue";
        },
      },
    });
  };

  const onEdit = (id: string) => {
    EditModalRef.current?.showOrHiddenModal(id);
  };

  const columns = useMemo<ColumnDef<ActivitySector>[]>(
    () => [
      {
        header: "Actions",
        enableSorting: false,
        footer: (props) => props.column.id,
        accessorFn: ({ id }) => (
          <Fragment>
            <Button variant="info" size="sm" onClick={() => onEdit(id)}>
              <FontAwesomeIcon icon={faEdit} />
            </Button>
            <Button
              variant="danger"
              size="sm"
              className="ms-2"
              onClick={() => DeleteModalRef.current?.showOrHiddenModal(id)}
            >
              <FontAwesomeIcon icon={faTrash} />
            </Button>
          </Fragment>
        ),
        id: "actions",
        cell: (info) => info.getValue(),
      },
      {
        header: "#",
        footer: (props) => props.column.id,
        accessorFn: ({ id }) => splitText(id, 10),
        id: "id",
        cell: (info) => info.getValue(),
      },
      {
        header: "Nom",
        footer: (props) => props.column.id,
        accessorFn: ({ name }) => name,
        id: "name",
        cell: (info) => info.getValue(),
      },
      {
        header: "Opco",
        footer: (props) => props.column.id,
        accessorFn: ({ opco }) => (
          <SoftBadge
            bg={opco?.backgroundColor ?? "danger"}
            textColor={opco?.textColor}
            className="me-2"
          >
            {opco?.name || "Inconnu"}
          </SoftBadge>
        ),
        id: "opcoId",
        cell: (info) => info.getValue(),
      },
      {
        header: "Entreprises",
        footer: (props) => props.column.id,
        accessorFn: ({ $extras }) => $extras?.companiesCount ?? 0,
        id: "companiesCount",
        cell: (info) => info.getValue(),
      },
      {
        header: "Sans SDR",
        footer: (props) => props.column.id,
        accessorFn: ({ $extras }) => $extras?.companiesWithoutSdrCount ?? 0,
        id: "companiesWithoutSdrCount",
        cell: (info) => info.getValue(),
      },
      {
        header: "Date de création",
        footer: (props) => props.column.id,
        accessorFn: ({ createdAt }) =>
          DateTime.fromISO(createdAt).toFormat("dd/MM/yyyy HH:mm"),
        id: "createdAt",
        cell: (info) => info.getValue(),
      },
    ],
    []
  );

  if (isLoading) {
    return <Loader />;
  }

  if (isError) {
    return <ErrorComponent error={error} />;
  }

  return (
    <div className="formalys-page formalys-list-page">
      <AreYouSurToContinueModal
        ref={DeleteModalRef}
        title="Êtes-vous sûr de vouloir supprimer ce secteur d'activité ?"
        onContinue={onDelete}
      />
      <StoreActivitySectorModal ref={EditModalRef} />
      <Table
        resource="activity-sectors"
        HeaderComponent={AllActivitySectorsHeader}
        tableName="activity-sectors"
        columns={columns}
        state={state}
        onStateChange={setState}
        data={data?.data}
        meta={data?.meta}
        isLoading={isLoading}
      />
    </div>
  );
};

export default ActivitySectorsView;
